import React, {useEffect, useState, useRef} from 'react'
import { connect } from 'react-redux'
import classnames from 'classnames/bind'
import PropTypes from 'prop-types'
import { Portal } from 'react-portal'

import { hideBodyScroll, showBodyScroll } from 'js/shared/utils'
import { closePopup } from '../PopupConstructor/actions/popupActions'

import styles from './PopupRedesign.module.scss'

const cx = classnames.bind(styles)

export const CloseButton = ({className, onClick, label = 'Close'}) => (
  <button
    type="button"
    className={cx('close', className)}
    onClick={onClick}
    aria-label={label}
  >
    <i aria-hidden={true} className={cx('close-icon', 'icon-modal-close')}/>
    <span className="sr-only">{label}</span>
  </button>
)
CloseButton.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string,
}

/**
 * <PopUp>
 *   Write your html here.
 * </PopUp>
 */
const PopUp = function (props) {
  const {
    className,
    isCloseBtn = true,
    closeClassName,
    bodyClassName,
    children,
    theme = 'default',
    onClose,
    useScrollBack = true,
    onCloseAction,
    autoFocus = true,
    ariaLabel = 'Dialog window',
    ariaLabelledBy,
    ariaDescribedBy,
    closeButtonLabel = 'Close',
  } = props;
  const [scrollTop, setScrollTop] = useState(window.pageYOffset)
  const modalWrapRef = useRef(null)

  useEffect(() => {
    const togglerElement = document.activeElement

    if (togglerElement?.hasAttribute('aria-expanded')) {
      togglerElement.setAttribute('aria-expanded', 'true')
    }

    hideBodyScroll()
    if (autoFocus) {
      focusElement(true)
    }
    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      showBodyScroll()
      if (useScrollBack && scrollTop) {
        window.scrollTo(0, scrollTop)
      }
      if (togglerElement?.hasAttribute('aria-expanded')) {
        togglerElement.setAttribute('aria-expanded', 'false')
      }
      togglerElement?.focus()
    }
  }, []);

  const onKeyDown = function (e) {
    if (e.keyCode === 27) {
      handleCloseClick()
    }
  }

  const getKeyboardFocusableElements = function (element) {
    const elements = element.querySelectorAll(
      'a[href], button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])'
    )
    return [...elements].filter(el => {
      return !el.hasAttribute('disabled')
        && !el.getAttribute('aria-hidden')
        && (el.getAttribute('tabindex') !== '-1')
    })
  }

  const handleCloseClick = () => {
    onClose ? onClose(onCloseAction) : onCloseAction()
  }

  const focusElement = function (isFirst) {
    const possibleElements = getKeyboardFocusableElements(modalWrapRef.current);
    const element = isFirst ? possibleElements[0] : possibleElements[possibleElements.length - 1]
    element?.focus({focusVisible: false})
  }

  return (
    <Portal>
      <div className={cx('root', `theme--${theme}`)}>
        <span aria-hidden={true} onFocus={() => focusElement(false)} tabIndex={0}/>
        <div
          className={cx('popup', className)}
          ref={modalWrapRef}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
        >
          <div className={cx('navigation')}>
            {isCloseBtn && (
              <CloseButton
                className={cx('close-btn', closeClassName)}
                onClick={handleCloseClick}
                label={closeButtonLabel}
              />
            )}
          </div>
          <div className={cx('popup__content', bodyClassName)}>
            {children}
          </div>
        </div>
        <div onClick={handleCloseClick} className={cx('background-layer')} aria-hidden={true}/>
        <span aria-hidden={true} onFocus={() => focusElement(true)} tabIndex={0}/>
      </div>
    </Portal>
  )
}

PopUp.propTypes = {
  onCloseAction: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  children: PropTypes.node.isRequired,
  theme: PropTypes.oneOf(['full', 'mobile-full', 'default', 'sticky-lath']),
  closeClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  className: PropTypes.string,
  isCloseBtn: PropTypes.bool,
  autoFocus: PropTypes.bool,
  useScrollBack: PropTypes.bool,
  ariaLabel: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  closeButtonLabel: PropTypes.string,
}

export default connect(() => ({}),
  dispatch => ({
    onCloseAction: () => dispatch(closePopup()),
  }))(PopUp)
